import React from 'react';
import { ProductAmountInputFormData } from '../ProductAmountInput';
import { Product, ProductVariant } from 'common/api/e-comm/models/Product';
import { useRootSelector } from 'rootStore';
import { selectPaymentType } from 'common/features/store/duck/ui/buy-now-flow';
import {
    PaymentMethod,
    PaymentType,
    PaymentTypeFee,
    getFeeForPaymentMethod,
} from 'common/api/e-comm/payment-types';
import { PartialRecord } from 'common/definitions/PartialRecord';
import { useStateRebateAdjustment } from 'common/hooks/useStateRebateAdjustment';
import { Btn } from 'components/btn/Btn';
import { Routes } from 'routes';
import { Link } from 'react-router-dom';
import { FormikProps, useFormikContext } from 'formik';
import { round } from 'common/utils/utils';
import Skeleton from 'react-loading-skeleton';

type BuyNowModalProps = {
    brandName: string;
    imageUrl: string;
    isOpen: boolean;
    itemDetails: ProductAmountInputFormData;
    onClose: () => void;
    onConfirmPurchase: (
        formikProps: FormikProps<ProductAmountInputFormData>,
        subtotal: number,
        fee: number,
        total: number
    ) => void;
    product: Product;
    paymentMethod?: PaymentMethod;
};

const getRebate = (
    itemDetails: BuyNowModalProps['itemDetails'],
    product: BuyNowModalProps['product'],
    adjustment: number,
    itemPrice: number
): [number, string] => {
    let percent = 0;

    if (product.options.length > 0) {
        const variant = product.variants.find(
            (variant: ProductVariant) => variant.price === itemPrice
        );
        percent = variant ? (variant?.bonus?.rebate || variant?.rebate) - adjustment : 0;
    } else {
        percent = (product.bonus?.rebate || product.rebate) - adjustment;
    }

    return [percent, `${round(itemPrice * itemDetails.quantity * (percent / 100), 2).toFixed(2)}`];
};

export interface BrandImageProps extends Pick<BuyNowModalProps, 'imageUrl'> {
    isOnBonus: boolean;
}

export const BrandImage = ({ imageUrl, isOnBonus }: BrandImageProps) => {
    return (
        <div className="relative m-auto w-24 sm:w-36 h-20 sm:h-28 p-1 border border-grey-9 text-center shadow shadow-2 rounded-xl flex items-center align-center justify-center">
            {imageUrl && <img src={imageUrl} />}
            {isOnBonus ? (
                <p className="absolute tracking-widest sm:tracking-normal leading-normal font-bold text-10px sm:text-xs py-0 bottom-0 w-full rounded-t-none rounded-xl text-white bg-bonus-pink">
                    BONUS
                </p>
            ) : null}
        </div>
    );
};

// Calculates is a product is on bonus or not
export const isProductOnBonus = (product: BuyNowModalProps['product'], value: number) => {
    if (product.options.length) {
        const variant = product.variants.find((variant) => variant.price === value);
        return Boolean(variant?.bonus?.rebate);
    } else {
        return Boolean(product.bonus?.rebate);
    }
};

const OrderDetails = (props: {
    brandName: string;
    subTotal: number;
    pricePerCard: number;
    itemDetails: BuyNowModalProps['itemDetails'];
    product: BuyNowModalProps['product'];
}) => {
    const {
        stateRebateAdjustment: { adjustment },
    } = useStateRebateAdjustment(props.product.isLocalProduct);
    const [earningPercent, rebate] = getRebate(
        props.itemDetails,
        props.product,
        adjustment,
        props.pricePerCard
    );
    const quantityText = `Qty ${props.itemDetails.quantity} x $${props.pricePerCard} per card`;
    const earningsText = `Earn ${earningPercent}% = `;

    return (
        <div className="text-grey-darkest text-base px-4">
            <div className="flex justify-between items-center">
                <span className="text-brand-dark w-48 sm:w-72 break-words font-bold">
                    {props.brandName}
                </span>
                <span>${round(props.subTotal, 2).toFixed(2)}</span>
            </div>
            <p>eGift Card</p>
            <p>{quantityText}</p>
            <p>
                {earningsText}
                <span className="text-success">${rebate}</span>
            </p>
        </div>
    );
};

interface PaymentDetailsProps extends Pick<BuyNowModalProps, 'paymentMethod'> {
    fee: number;
}

const PaymentDetails = ({ paymentMethod, fee }: PaymentDetailsProps) => {
    const [label, accountLastFour] = useRootSelector((s) => selectPaymentType(s, paymentMethod));

    return (
        <div className="text-grey-darkest text-base px-4">
            <p className="text-brand-dark font-bold">Payment Details</p>
            <div className="flex justify-between">
                {accountLastFour ? (
                    <>
                        <span>
                            {label} •••• {accountLastFour}
                        </span>
                        <span>${round(fee, 2).toFixed(2)}</span>
                    </>
                ) : (
                    <span className="w-full h-6">
                        <Skeleton className="w-full h-6" />
                    </span>
                )}
            </div>
        </div>
    );
};

const Total = ({ children }: { children: React.ReactNode }) => {
    return (
        <div className="flex justify-between items-center px-4">
            <p className="text-base text-brand-dark font-bold">Total</p>
            <p className="text-2xl text-brand-dark font-bold">{children}</p>
        </div>
    );
};

export const getPrice = (
    itemDetails: BuyNowModalProps['itemDetails'],
    product: BuyNowModalProps['product']
) => {
    if (product.options.length) {
        const selectedOption = itemDetails.selectedOptions[product.options[0].id];
        return parseInt(selectedOption.substr(1));
    } else {
        return itemDetails.priceOverride!;
    }
};

const getTotal = (
    itemDetails: BuyNowModalProps['itemDetails'],
    price: number,
    paymentTypeFee?: PaymentTypeFee | null
): [number, number, number] => {
    // NOTE: Error handling
    if (paymentTypeFee === null) {
        return [0, 0.0, 0];
    }

    const value = price * itemDetails.quantity;
    const fee =
        (paymentTypeFee?.isPercentage
            ? value * (paymentTypeFee.amount / 100)
            : paymentTypeFee?.amount) || 0;
    return [value, round(value + fee, 2), fee];
};

export const getPaymentTypeFee = (
    paymentTypeFees: PartialRecord<PaymentType, PaymentTypeFee> | null,
    paymentMethod?: PaymentMethod | null
) => {
    if (!paymentMethod || paymentTypeFees === null) {
        return {
            paymentType: PaymentType.CreditCard,
            paymentSubType: null,
            amount: 0,
            isPercentage: false,
        } as PaymentTypeFee;
    }

    return getFeeForPaymentMethod(paymentTypeFees, paymentMethod);
};

type PickDetailProps =
    | 'itemDetails'
    | 'product'
    | 'paymentMethod'
    | 'onConfirmPurchase'
    | 'brandName';
interface DetailsProps extends Pick<BuyNowModalProps, PickDetailProps> {
    price: number;
}

const Details = ({
    itemDetails,
    product,
    paymentMethod,
    onConfirmPurchase,
    price,
    brandName,
}: DetailsProps) => {
    const paymentTypeFees = useRootSelector((s) => s.store.organization.paymentTypeFees);
    const paymentTypeFee = getPaymentTypeFee(paymentTypeFees, paymentMethod);
    const [subTotal, total, fee] = getTotal(itemDetails, price, paymentTypeFee);
    const formikProps = useFormikContext<ProductAmountInputFormData>();

    const onPurchaseClick = () => {
        onConfirmPurchase(formikProps, subTotal, fee, total);
    };

    const formattedTotal = `$${total.toFixed(2)}`;

    return (
        <div className="mt-3 sm:mt-5 sm:px-7">
            <OrderDetails
                itemDetails={itemDetails}
                product={product}
                pricePerCard={price}
                brandName={brandName}
                subTotal={subTotal}
            />
            <hr className="my-4 border-grey-light" />
            <PaymentDetails paymentMethod={paymentMethod} fee={fee} />
            <hr className="my-4 border-grey-light" />
            <Total>{formattedTotal}</Total>
            <br />
            <Btn className="block m-auto" onClick={onPurchaseClick} disabled={!paymentMethod}>
                Confirm Purchase
            </Btn>
        </div>
    );
};

interface NonBuyNowDetailsProps extends Omit<BuyNowModalProps, 'isOpen' | 'onClose'> {
    price: number;
    isOnBonus: boolean;
}

export const BuyNowDetails = (props: NonBuyNowDetailsProps) => {
    const price = props.price;
    const isOnBonus = props.isOnBonus;

    return (
        <>
            <BrandImage isOnBonus={isOnBonus} imageUrl={props.imageUrl} />
            <Details
                itemDetails={props.itemDetails}
                onConfirmPurchase={props.onConfirmPurchase}
                paymentMethod={props.paymentMethod}
                product={props.product}
                price={price}
                brandName={props.brandName}
            />
            <Disclaimer />
        </>
    );
};

const Disclaimer = () => {
    return (
        <div className="mt-5 text-xs text-grey-medium text-center">
            <p>
                By completing this order, I agree to the{' '}
                <a href={Routes.TermsOfUse}>Terms of Use</a>
                {'. '}
            </p>
            <p>Gift cards are not returnable or refundable after purchase.</p>
        </div>
    );
};
